import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import type {
  ObsDatasetVersion,
  ObsScreen,
  ObsUploadResult,
  ObsValidationReport,
} from "@/types/obs";

/**
 * OBS-Reports dataset API.
 *
 * ⚠️ THESE ROUTES ARE DELIBERATELY *NOT* WRAPPED IN THE {success,message,data}
 * ENVELOPE, unlike every other governance endpoint. `/api/obs-datasets` is
 * absent from ENVELOPE_PREFIXES in the backend on purpose: the envelope
 * DISCARDS the body on any status >= 400, and the whole point of a validation
 * failure here is the report explaining WHY — an operator who is told only
 * "Request failed" cannot fix their workbook.
 *
 * The consequence for this file: responses come back RAW, so there is no
 * `unwrapData`, and a 422/409 is a normal return value rather than a throw
 * (http-client only rejects on a `success:false` envelope). A genuine Strapi
 * error (401/403/404) still arrives as `{data:null, error:{...}}`, which looks
 * nothing like a report — `asReport` tells them apart rather than letting an
 * auth failure render as an empty, reassuring validation result.
 */

interface StrapiErrorBody {
  error?: { status?: number; name?: string; message?: string };
}

function throwIfStrapiError(payload: unknown): void {
  const err = (payload as StrapiErrorBody | null)?.error;
  if (err && (err.message || err.name)) {
    throw new Error(err.message || err.name || "فشل الطلب");
  }
}

function asReport<T extends ObsValidationReport>(payload: unknown): T {
  throwIfStrapiError(payload);
  const report = payload as T | null;
  if (!report || typeof report.accepted !== "boolean") {
    throw new Error("استجابة غير متوقعة من الخادم أثناء التحقق من الملف");
  }
  return report;
}

export const obsService = {
  /** The six screens and each one's version history. */
  async list(): Promise<ObsScreen[]> {
    const response = await http.get(API_ENDPOINTS.OBS_DATASETS.LIST);
    throwIfStrapiError(response);
    return (Array.isArray(response) ? response : []) as ObsScreen[];
  },

  /**
   * Dry run — changes nothing. No S3 object, no version, no activation.
   * This is what the operator sees before committing.
   */
  async validate(slug: string, file: File): Promise<ObsValidationReport> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await http.upload(
      "POST",
      API_ENDPOINTS.OBS_DATASETS.VALIDATE(slug),
      formData,
    );
    return asReport<ObsValidationReport>(response);
  },

  /**
   * Publish. `force` acknowledges WARNINGS only — errors are never overridable,
   * by the backend, not by this flag.
   */
  async upload(
    slug: string,
    file: File,
    force = false,
  ): Promise<ObsUploadResult> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await http.upload(
      "POST",
      API_ENDPOINTS.OBS_DATASETS.UPLOAD(slug, force),
      formData,
    );
    return asReport<ObsUploadResult>(response);
  },

  /** Roll back (or forward) to an existing version. No file, cannot fail on parse. */
  async activateVersion(
    slug: string,
    version: number,
  ): Promise<ObsDatasetVersion> {
    const response = await http.post(
      API_ENDPOINTS.OBS_DATASETS.ACTIVATE(slug, version),
      {},
    );
    throwIfStrapiError(response);
    return response as ObsDatasetVersion;
  },
};
